import { useEffect } from "react";
import { Box, Button, Group, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Panel, Prompt, Screen, LoadError } from "../components/primitives";
import { useConsoleStore } from "../stores/consoleStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import css from "./screens.module.css";

export default function Billing() {
  const workspaceId = useWorkspaceStore((s) => s.currentId);
  const billing = useConsoleStore((s) => s.billing);
  const loadError = useConsoleStore((s) => s.errors.billing);
  const load = useConsoleStore((s) => s.loadBilling);

  useEffect(() => {
    if (workspaceId) void load();
  }, [workspaceId, load]);

  if (!billing) {
    return (
      <Screen>
        <Prompt command="vf billing status" />
        <LoadError message={loadError} />
      </Screen>
    );
  }

  const notImplemented = (what: string) =>
    notifications.show({ message: `${what} has no endpoint yet — see ROADMAP.md`, color: "yellow" });

  return (
    <Screen>
      <Prompt command="vf billing status" />

      <Box style={{ maxWidth: 960 }}>
        <Box
          mb={16}
          style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}
          className={css.split2}
        >
          <Box p="16px 18px" style={{ border: "1px solid var(--vf-border)", background: "var(--vf-panel)" }}>
            <Group justify="space-between" align="flex-start" mb={14} wrap="nowrap">
              <Box>
                <div className={css.statKey}>current plan</div>
                <Text fz="20px" fw={600} c="var(--vf-acc)">
                  {billing.plan}
                </Text>
              </Box>
              <Button
                variant="light"
                size="compact-sm"
                onClick={() => notImplemented("plan management")}
                styles={{
                  root: {
                    background: "var(--vf-accbg)",
                    border: "1px solid var(--vf-border)",
                    color: "var(--vf-acc)",
                    padding: "8px 14px",
                    height: "auto",
                    fontSize: "12.5px",
                  },
                }}
              >
                manage plan
              </Button>
            </Group>
            <Text fz="12.5px" c="var(--vf-sec)" lh={1.6}>
              {billing.blurb}
            </Text>
          </Box>

          <Box p="16px 18px" style={{ border: "1px solid var(--vf-border)", background: "var(--vf-panel)" }}>
            <div className={css.statKey}>est. this month</div>
            <Text fz="26px" fw={600} c="var(--vf-acc)" mb={6}>
              {billing.estimate}
            </Text>
            <Text fz="11.5px" c="var(--vf-dim2)">
              next invoice {billing.nextDate}
            </Text>
          </Box>
        </Box>

        <Box mb={16}>
          <Panel title="usage this cycle · billed meters">
            <Box p={16}>
              {billing.meters.map((m) => (
                <Box key={m.label} mb={12}>
                  <div className={css.meterHead}>
                    <span style={{ color: "var(--vf-text)" }}>
                      {m.label}
                      {m.estimated ? " (est.)" : ""}
                    </span>
                    <span style={{ color: "var(--vf-sec)" }}>
                      {m.used}
                      {m.amount ? ` · ${m.amount}` : ""}
                    </span>
                  </div>
                  {m.rate ? (
                    <div style={{ color: "var(--vf-dim)", fontSize: "11px" }}>{m.rate}</div>
                  ) : null}
                </Box>
              ))}
            </Box>
          </Panel>
        </Box>

        {billing.quotas.length > 0 && (
          <Box mb={16}>
            <Panel title="free tier quotas · enforced">
              <Box p={16}>
                {billing.quotas.map((q) => (
                  <Box key={q.label} mb={14}>
                    <div className={css.meterHead}>
                      <span style={{ color: "var(--vf-text)" }}>{q.label}</span>
                      <span style={{ color: "var(--vf-sec)" }}>
                        {q.used} / {q.cap}
                      </span>
                    </div>
                    <div className={css.meterTrack}>
                      <div className={css.meterFill} style={{ width: `${Math.round(q.pct * 100)}%` }} />
                    </div>
                  </Box>
                ))}
              </Box>
            </Panel>
          </Box>
        )}

        <Box style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }} className={css.split2}>
          <Panel title="invoices">
            {billing.invoices.length === 0 ? (
              <Box p="30px 20px" ta="center" fz="12px" c="var(--vf-dim2)">
                No invoices yet — the first one is issued at the end of the cycle.
              </Box>
            ) : (
              billing.invoices.map((iv) => (
                <div key={iv.period} className={css.invoiceRow}>
                  <span style={{ color: "var(--vf-text)" }}>{iv.period}</span>
                  <span style={{ color: "var(--vf-acc)" }}>{iv.amount}</span>
                  <span style={{ color: "var(--vf-ok)" }}>● {iv.status}</span>
                  <button
                    type="button"
                    className={css.dismiss}
                    style={{ textAlign: "right" }}
                    onClick={() => notImplemented("invoice download")}
                  >
                    pdf
                  </button>
                </div>
              ))
            )}
          </Panel>

          <Panel title="payment method">
            <Box p={16} fz="12.5px" style={{ lineHeight: 2 }}>
              <Row k="card" v={billing.card} />
              <Row k="expires" v={billing.exp} />
              <Row k="billing email" v={billing.billingEmail} />
              <Button
                fullWidth
                mt={12}
                variant="light"
                onClick={() => notImplemented("card update")}
                styles={{
                  root: {
                    background: "var(--vf-accbg)",
                    border: "1px solid var(--vf-border)",
                    color: "var(--vf-acc)",
                    padding: 9,
                    height: "auto",
                    fontSize: "12.5px",
                  },
                }}
              >
                update card
              </Button>
            </Box>
          </Panel>
        </Box>
      </Box>
    </Screen>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
      <span style={{ color: "var(--vf-dim)" }}>{k}</span>
      <span style={{ color: "var(--vf-text)" }}>{v}</span>
    </div>
  );
}
